/**
 * Provider registry. Maps provider id ('google' | 'apple' | 'caldav' | 'ical')
 * to the concrete EnrichmentProvider implementation.
 *
 * Add new providers here when their implementations land. The hook never
 * touches concrete provider modules directly — it dispatches via this map,
 * which is the load-bearing seam for the provider abstraction.
 *
 * The factories accept opts so the EnrichmentCacheProvider can wire in
 * test-only fetch / baseUrl overrides without each provider needing its
 * own context.
 */

import { createGoogleCalendarEnrichmentProvider } from "./google";
import { validateEnrichmentProvider } from "./types";

/**
 * @typedef {Object} ProviderRegistryOpts
 * @property {typeof fetch} [fetch]   — overridable fetch (default: globalThis.fetch)
 * @property {string} [googleBaseUrl] — override for the Google API base (e2e mock server)
 */

/**
 * Build the provider registry. Returning a fresh object per call keeps the
 * registry mockable in tests (no module-level singleton state).
 *
 * @param {ProviderRegistryOpts} [opts]
 * @returns {Record<string, import('./types').EnrichmentProvider>}
 */
export function createProviderRegistry(opts = {}) {
  const registry = {
    google: createGoogleCalendarEnrichmentProvider({
      fetch: opts.fetch,
      baseUrl: opts.googleBaseUrl,
    }),
  };

  for (const [id, provider] of Object.entries(registry)) {
    const { ok, errors } = validateEnrichmentProvider(provider);
    if (!ok) {
      throw new Error(
        `Provider '${id}' fails the EnrichmentProvider contract: ${errors.join("; ")}`,
      );
    }
  }

  return registry;
}

/**
 * Pick the provider for a given provider id. Returns undefined if the id
 * is unknown — callers (the hook) must surface 'permission_denied' for
 * unknown providers so the UI can prompt re-link instead of looping.
 *
 * @param {Record<string, import('./types').EnrichmentProvider>} registry
 * @param {string} providerId
 * @returns {import('./types').EnrichmentProvider | undefined}
 */
export function pickProvider(registry, providerId) {
  if (!registry || typeof providerId !== "string") return undefined;
  return registry[providerId];
}
